import { formatCurrency } from "@/utils/formatCurrency";
import { useLocation } from "react-router-dom";
import { useActionBarHeight } from "@/hooks/use-action-bar-height";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { TopupCheckout, TopupPreview } from "@/services/topup.service";
import { useAuthStore } from "@/stores/auth.store";
import { useEffect, useMemo, useState } from "react";
import type { RouteTopUp } from "@/types/topup/checkout";
import type { Route, TopupPreviewData } from "@/types/topup/preview";
import type { TopupRouteEntry } from "@/lib/topup";
import { TopupBreakDownDetail } from "@/features/topup/TopupBreakDownDetail";
import TopupDetailsSkeleton from "./TopupDetailsSkeleton";
import { FullScreenLoader } from "@/components/common/FullScreenLoader";

  export default function TopupDetailsPage() {
  const { state } = useLocation();
  const selectedRoutes = useMemo(
    () => (state as { entries?: TopupRouteEntry[] } | null)?.entries ?? [],
    [state],
  );

  const [previewDetail, setPreviewDetail] = useState<TopupPreviewData | null>(null);
  // Expose the fixed action bar height so the layout's bottom-hero sits above it
  const footerRef = useActionBarHeight<HTMLElement>();
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    // Clear the loader if the page is restored from bfcache (e.g. user hits back
    // from the payment gateway) instead of a fresh navigation.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setCheckoutLoading(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);

    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    const agentCompanySellerId =
      useAuthStore.getState().user?.agentCompanySellerId ?? "";

    if (!agentCompanySellerId) return;

    const fetchPreviewTopup = async () => {
      try {
        setLoading(true);
        const routeList: Route[] = selectedRoutes
          .filter((route) => route.operatorCompanyRouteId)
          .map((route) => ({
            operatorCompanyRouteId: route.operatorCompanyRouteId!,
            ticketList: Object.entries(route.counts)
              .filter(([, quantity]) => quantity > 0)
              .map(([mdTicketTypeId, quantity]) => ({
                mdTicketTypeId,
                quantity,
              })),
          }))
          .filter((route) => route.ticketList.length > 0);

        const response = await TopupPreview({
          agentCompanySellerId: agentCompanySellerId,
          routeList,
        });

        await new Promise((resolve) => setTimeout(resolve, 300));

        setPreviewDetail(response.data);
      } catch (error) {
        toast.error(
          error instanceof Error
            ? error.message
            : "Failed to load booking details.",
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPreviewTopup();
  }, [selectedRoutes]);

  const handleOnlinePayment = async () => {
    try {
      setCheckoutLoading(true);

      const routeTopUpList: RouteTopUp[] = (previewDetail?.routeList ?? [])
        .map((route) => ({
          operatorCompanyRouteId: route.operatorCompanyRouteId,
          topUpList: route.ticketList
            .filter((ticket) => ticket.quantity > 0)
            .map((ticket) => ({
              mdTicketTypeId: ticket.mdTicketTypeId,
              quantity: ticket.quantity,
            })),
        }))
        .filter((route) => route.topUpList.length > 0);

      if (routeTopUpList.length === 0) {
        throw new Error("No top-up tickets selected.");
      }

      const response = await TopupCheckout({
        agentCompanySellerId:
          useAuthStore.getState().user?.agentCompanySellerId ?? "",
        redirectUrl: `${window.location.origin}/top-up/top-up-confirmation/`,
        totalPrice: previewDetail?.totalPrice ?? 0,
        routeTopUpList,
      });

      const checkoutUrl = response.data.redirectUrl;

      if (!checkoutUrl) {
        throw new Error("Payment redirect URL not available.");
      }

      window.location.href = checkoutUrl;
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to proceed with online payment.",
      );
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 pt-4 pb-16 sm:px-6 lg:px-8">
      {checkoutLoading && <FullScreenLoader />}
      <h1 className="text-secondary-foreground text-lg font-semibold">
        Top-up Details
      </h1>

      <div className="mt-2 flex flex-col gap-6">
        {loading ? (
          <TopupDetailsSkeleton />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-[2fr_1fr]">
            <div className="space-y-6">
              {previewDetail?.routeList?.map((route) => (
                <TopupBreakDownDetail key={route.routeName} route={route} />
              ))}
            </div>
            <div className="hidden md:block">
              <div className="rounded-2xl bg-card px-4 py-6 shadow-xl">
                <div className="flex items-center justify-between pb-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    Total
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    {formatCurrency(previewDetail?.totalPrice ?? 0)}
                  </p>
                </div>
                <div className="flex items-center justify-between pb-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    Processing Fee
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    {formatCurrency(previewDetail?.processingFee ?? 0)}
                  </p>
                </div>
                <div className="flex items-center justify-between pb-3">
                  <p className="text-sm font-medium text-muted-foreground">
                    Commission
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    -{formatCurrency(previewDetail?.commissionAmount ?? 0)}
                  </p>
                </div>
                <div className="flex items-center justify-between pb-8">
                  <p className="text-sm font-medium text-muted-foreground">
                    Final Price
                  </p>
                  <p className="text-sm font-medium text-muted-foreground">
                    {formatCurrency(previewDetail?.finalPrice ?? 0)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full cursor-pointer"
                  onClick={handleOnlinePayment}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? "Processing..." : "Online Payment"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      <footer
        ref={footerRef}
        className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-card md:hidden"
      >
        <div className="mx-auto w-full flex flex-col max-w-7xl justify-between gap-2 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-base font-semibold text-secondary-foreground">
              <span>Total</span>
              <span>{formatCurrency(previewDetail?.totalPrice ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-base font-semibold text-secondary-foreground">
              <span>Processing Fee</span>
              <span>{formatCurrency(previewDetail?.processingFee ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-base font-semibold text-secondary-foreground">
              <span>Commission</span>
              <span>-{formatCurrency(previewDetail?.commissionAmount ?? 0)}</span>
          </div>
          <div className="flex items-center justify-between text-lg font-semibold text-secondary-foreground">
              <span>Final Price</span>
              <span>{formatCurrency(previewDetail?.finalPrice ?? 0)}</span>
          </div>

          <Button
            type="button"
            variant="secondary"
            className="w-full cursor-pointer"
            onClick={handleOnlinePayment}
            disabled={checkoutLoading}
          >
            {checkoutLoading ? "Processing..." : "Online Payment"}
          </Button>
        </div>
      </footer>
    </div>
  );
}
